import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable, when } from 'mobx';
import styled from '@emotion/styled';
import { ExitSurveyConfigStore, ExitSurveyReasonStore, ExitSurveyResponseStore } from '@seedlang/stores';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { xorBy } from 'lodash';
import Spinner from 'components/spinner';
import Text from 'components/text';
import ReasonList from 'components/user/reason_list';
import Stack, { gaps } from 'components/layout/stack';
import Button from 'components/button/button';
import Link from 'components/link';

const Wrapper = styled.div`
  background: #FFF;
  display: flex;
  flex-direction: column;
  margin: ${gaps.md} auto;
  max-width: 600px;

  @media (min-width: 700px) {
    border-radius: 20px;
    overflow: ${props => props.hasBanner ? 'hidden' : 'visible'};
  }
`;

const ContentWrapper = styled.div`
  padding: ${gaps.md};
  display: flex;
  flex-direction: column;
  gap: ${gaps.lg};

  @media (min-width: 700px) {
    padding: ${gaps['2xl']};
  }
`;

const BannerImage = styled.img`
  width: 100%;
  display: block;
  object-fit: cover;
  max-height: 200px;
`;

const LinkButton = styled(Link)`
  text-decoration: underline;
  color: ${() => AppUI.site.accentColor};
  cursor: pointer;
  font-size: 14px;
`;

const MAX_RESPONSE_RETRIES = 20;
const RESPONSE_RETRY_INTERVAL = 1000;

@observer
class ExitSurvey extends React.Component {

  @observable selectedOption;
  @observable selectedOptions = [];
  @observable whatCouldWeHaveDoneBetterResponse = '';
  @observable responseLoaded = false;

  constructor(props) {
    super(props);
    if (props.preview) {
      ExitSurveyResponseStore.setShowData({});
      this.responseLoaded = true;
      this.whenDisposer = () => {};
      return;
    }
    ExitSurveyConfigStore.getShow();
    ExitSurveyReasonStore.getIndex({ queryStrings: { active: true } });
    this.fetchResponseWithRetry();
    this.whenDisposer = when(
      () => ExitSurveyConfigStore.hasShowData && ExitSurveyReasonStore.hasIndexData && ExitSurveyResponseStore.hasShowData,
      () => this.prefillFromResponse(),
    );
  }

  componentWillUnmount() {
    this.whenDisposer();
    clearTimeout(this.retryTimeout);
    ExitSurveyResponseStore.setShowData(null);
  }

  fetchResponseWithRetry = (attempt = 0) => {
    ExitSurveyResponseStore.getShow({}, (resp) => {
      if (resp?.id) {
        ExitSurveyResponseStore.setShowData(resp);
        this.responseLoaded = true;
        return;
      }
      if (attempt < MAX_RESPONSE_RETRIES - 1) {
        this.retryTimeout = setTimeout(() => this.fetchResponseWithRetry(attempt + 1), RESPONSE_RETRY_INTERVAL);
      } else {
        this.responseLoaded = true;
      }
    });
  }

  prefillFromResponse = () => {
    const response = ExitSurveyResponseStore.showData;
    if (!response?.id) return;

    const responseReasonIds = new Set(response.reasons.map(r => r.exitSurveyReasonId));
    const matchedReasons = this.reasons.filter(r => responseReasonIds.has(r.id));

    if (this.config.multipleChoice) {
      this.selectedOptions = matchedReasons;
    } else if (matchedReasons.length > 0) {
      this.selectedOption = matchedReasons[0];
    }

    if (response.whatCouldWeHaveDoneBetterResponse) {
      this.whatCouldWeHaveDoneBetterResponse = response.whatCouldWeHaveDoneBetterResponse;
    }
  }

  @computed get loading() {
    return (
      ExitSurveyConfigStore.hasOutstandingRequests ||
      ExitSurveyReasonStore.hasOutstandingRequests ||
      ExitSurveyResponseStore.hasOutstandingRequests ||
      !this.responseLoaded
    );
  }

  @computed get config() {
    return ExitSurveyConfigStore.showData;
  }

  @computed get reasons() {
    if (this.props.preview) {
      return ExitSurveyReasonStore.indexData.filter(r => r.active);
    }
    return ExitSurveyReasonStore.indexData;
  }

  @computed get canSubmit() {
    return this.config.multipleChoice ? this.selectedOptions.length > 0 : this.selectedOption;
  }

  onSelect = (item) => {
    if (this.config.multipleChoice) {
      this.selectedOptions = xorBy(this.selectedOptions, [item], 'id');
      return;
    }
    this.selectedOption = this.selectedOption?.id === item.id ? null : item;
  }

  onSubmit = () => {
    if (this.props.preview) return;

    const responseId = ExitSurveyResponseStore.showData?.id;
    if (!responseId) return;

    const selected = this.config.multipleChoice ? this.selectedOptions : [this.selectedOption];
    ExitSurveyResponseStore.update({
      ids: { exitSurveyResponseId: responseId },
      data: {
        what_could_we_have_done_better_response: this.whatCouldWeHaveDoneBetterResponse,
        reasons: selected.map(r => ({
          exit_survey_reason_id: r.id,
        })),
      },
    }, () => AppUI.routeStore.routeToNamed('settings.billing'));
  }

  render() {
    if (this.loading) {
      return <Wrapper><Spinner /></Wrapper>;
    }

    const hasBanner = isPresent(this.config?.image?.url);

    return (
      <Wrapper hasBanner={hasBanner}>
        {
          hasBanner &&
            <BannerImage
              src={this.config.image.url}
              alt=''
            />
        }
        <ContentWrapper>
          <Stack gap='sm' alignItems='stretch'>
            <Text heading='3'>
              Sorry to see you go!
            </Text>
            <Text>
              Please help us improve our membership by letting us know why you are leaving.
            </Text>
          </Stack>
          <ReasonList
            options={this.reasons}
            multipleChoice={this.config.multipleChoice}
            selectedOption={this.selectedOption}
            selectedOptions={this.selectedOptions}
            onSelect={this.onSelect}
          />
          <Stack gap='sm' alignItems='stretch'>
            <Text bold>What could we have done better?</Text>
            <textarea
              rows={4}
              value={this.whatCouldWeHaveDoneBetterResponse}
              onChange={e => { this.whatCouldWeHaveDoneBetterResponse = e.target.value; }}
            />
          </Stack>
          <Stack inline alignItems='center' justifyContent='space-between'>
            <Button disabled={!this.canSubmit} onClick={this.onSubmit}>Submit</Button>
            <LinkButton
              to='settings.billing'
              onClick={this.props.preview ? (e) => e.preventDefault() : undefined}
            >
              Skip
            </LinkButton>
          </Stack>
        </ContentWrapper>
      </Wrapper>
    );
  }
}

export default ExitSurvey;
